import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  alpha,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { formatProduct, getMarginType } from '../../../utils/utilfunction';
import { exchangeSpecificStringifiedNumber } from '../../../services/exchanges';

const PositionDetailsDrawer = ({
  open,
  onClose,
  position,
  conversionMode,
  setConversionMode,
  quantity,
  error,
  handleQuantityChange,
  handleConvert,
  getTargetProductType,
  onAddMore
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  if (!position) return null;

  // Determine position side
  let positionSide = "CLOSED";
  let sideColor = theme.palette.text.secondary;
  let showPositionChip = true;

  if (position.netBuyQty > position.netSellQty) {
    positionSide = "BUY";
    sideColor = theme.palette.success.main;
  } else if (position.netSellQty > position.netBuyQty) {
    positionSide = "SELL";
    sideColor = theme.palette.error.main;
  } else if (position.buy_quantity === 0 && position.sell_quantity === 0) {
    // Hide the chip completely when both quantities are zero
    showPositionChip = false;
  }

  // Format currency values
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "—";
    return `₹${exchangeSpecificStringifiedNumber(position.exchange, Math.abs(value))}`;
  };

  // Format quantity values
  const formatQuantity = (value) => {
    if (value === undefined || value === null) return "—";
    return value;
  };

  const targetProduct = conversionMode ? getTargetProductType(position.product, position.exchange) : null;

  // Determine if P&L is positive or negative
  const isPnlPositive = (position.pnl || 0) > 0;
  const pnlColor = isPnlPositive ? theme.palette.success.main : (position.pnl < 0 ? theme.palette.error.main : theme.palette.text.secondary);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '380px',
          height: isMobile ? 'auto' : '100%',
          maxHeight: isMobile ? '90vh' : '100%',
          bgcolor: theme.palette.background.paper,
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden'
      }}>
        {/* Header with drag indicator for mobile */}
        {isMobile && (
          <Box sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            pt: 1.5,
            pb: 0.5
          }}>
            <Box sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: theme.palette.divider
            }} />
          </Box>
        )}

        {/* Main Header - Fixed */}
        <Box sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          {conversionMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => setConversionMode(false)}
                size="small"
                sx={{
                  mr: 1.5,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                Convert Position
              </Typography>
            </Box>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Position Details
            </Typography>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: alpha(theme.palette.text.secondary, 0.1),
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.divider,
            borderRadius: '4px',
          }
        }}>
          <Box sx={{ p: 2.5 }}>
            {!conversionMode ? (
              <>
                {/* Symbol and Position Info */}
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {position.trading_symbol}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {position.exchange}
                        </Typography>
                        <Chip
                          label={formatProduct(position.product)}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            borderRadius: '4px',
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                    </Box>
                    {showPositionChip && (
                      <Chip
                        label={`${positionSide} ${Math.abs(position.net_quantity)}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(sideColor, 0.1),
                          color: sideColor,
                          fontWeight: 600,
                          borderRadius: '4px',
                          height: 24,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                  }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Price
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(position.ltp)}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        P&L
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isPnlPositive ?
                          <TrendingUpIcon fontSize="small" sx={{ color: pnlColor, mr: 0.5 }} /> :
                          <TrendingDownIcon fontSize="small" sx={{ color: pnlColor, mr: 0.5 }} />
                        }
                        <Typography variant="h6" sx={{ fontWeight: 600, color: pnlColor }}>
                          {formatCurrency(Math.abs(position.pnl))}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Buy Details - Only show if there's buy quantity */}
                {position.netBuyQty > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                  }}>
                    <Box sx={{
                      width: 4,
                      height: 16,
                      borderRadius: 1,
                      bgcolor: theme.palette.success.main,
                      mr: 1.5
                    }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        letterSpacing: '0.5px'
                      }}
                    >
                      BUY DETAILS
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.background.paper, 0.7),
                  }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Quantity
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatQuantity(position.netBuyQty)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Avg. Price
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(position.average_buy_price || position.buyAverage)}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Total Value
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(position.netBuyValue)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                )}

                {/* Sell Details - Only show if there's sell quantity */}
                {position.netSellQty > 0 && (
                <Box>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                  }}>
                    <Box sx={{
                      width: 4,
                      height: 16,
                      borderRadius: 1,
                      bgcolor: theme.palette.error.main,
                      mr: 1.5
                    }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 600,
                        letterSpacing: '0.5px'
                      }}
                    >
                      SELL DETAILS
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.background.paper, 0.7),
                  }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Quantity
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatQuantity(position.netSellQty)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Avg. Price
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(position.average_sell_price || position.sellAverage)}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Total Value
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(position.netSellValue)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                )}
              </>
            ) : (
              <>
                {/* Conversion Mode */}
                <Box sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: 2,
                  bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {position.trading_symbol}
                  </Typography>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2.5
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {getMarginType(position.product)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Current
                      </Typography>
                    </Box>

                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.divider
                    }}>
                      <SwapHorizIcon />
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main
                        }}
                      >
                        {getMarginType(targetProduct)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Target
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">Available Quantity</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {Math.abs(position?.net_quantity)}
                    </Typography>
                  </Box>
                </Box>

                {/* Quantity Input */}
                <Box sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                  border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.divider, 0.5)}`
                }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    QUANTITY TO CONVERT
                  </Typography>

                  <input
                    type="text"
                    value={quantity}
                    onChange={handleQuantityChange}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.divider, 0.7)}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: isDarkMode ? alpha(theme.palette.background.default, 0.3) : alpha(theme.palette.background.default, 0.5),
                      color: theme.palette.text.primary,
                      outline: 'none',
                      fontWeight: '500'
                    }}
                  />

                  {error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {error}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Footer - Fixed */}
        <Box sx={{
          p: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          gap: 2,
          bgcolor: theme.palette.background.paper,
          position: 'sticky',
          bottom: 0,
          zIndex: 10
        }}>
          {!conversionMode ? (
            <>
              {positionSide !== "CLOSED" && (
                <Button
                  variant="contained"
                  color={positionSide === "BUY" ? "success" : "error"}
                  onClick={() => {
                    // Use the already determined positionSide variable
                    const orderType = positionSide === "BUY" ? "BUY" : "SELL";
                    // This will open the order window with the selected position
                    onAddMore(position, orderType);
                    onClose(); // Close the drawer after opening the order window
                  }}
                  sx={{
                    height: 40,
                    minWidth: 90,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: '14px',
                    flex: 1,
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    boxShadow: 'none',
                    letterSpacing: 1,
                    padding: '26px 16px',
                    '&:hover': {
                      backgroundColor: positionSide === "BUY" ? theme.palette.success.dark : theme.palette.error.dark,
                      boxShadow: '0 2px 5px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  {positionSide === "BUY" ? "Buy More" : "Sell More"}
                </Button>
              )}

              {/* 
              <Button
                fullWidth
                variant="contained"
                onClick={() => setConversionMode(true)}
                disabled={position.net_quantity === 0}
                sx={{
                  height: 48,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 'none',
                  flex: 1,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }
                }}
                startIcon={<SwapHorizIcon />}
              >
                Convert Position
              </Button>
              */}
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => setConversionMode(false)}
                sx={{
                  height: 48,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  borderRadius: 2,
                  flex: 1,
                  whiteSpace: 'nowrap',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.text.primary, 0.02)
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConvert}
                disabled={!quantity || !!error}
                sx={{
                  height: 48,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  flex: 2,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.common.white
                  }
                }}
              >
                Convert
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default PositionDetailsDrawer; 